import { appClient } from 'api/apiClient';
import { buildPageQuery, type Paginated } from 'api/v1Helpers';
import {
    normalizeMember,
    type ApiMember,
    type Member,
    type MemberDonationsPage,
    type MemberDonationsQuery,
    type UpdateMemberPayload,
} from 'types/members';

export async function fetchMembers(
    parishId: string,
    opts?: { page?: number; pageSize?: number; search?: string }
): Promise<Paginated<Member>> {
    const params = buildPageQuery(opts?.page ?? 1, opts?.pageSize ?? 20, {
        search: opts?.search,
    });
    const page = await appClient.get<Paginated<ApiMember>>(`parishes/${parishId}/members?${params.toString()}`);
    return {
        items: (page.items || []).map(normalizeMember),
        total: page.total ?? 0,
        page: page.page ?? opts?.page ?? 1,
        pageSize: page.pageSize ?? opts?.pageSize ?? 20,
    };
}

export async function fetchMember(parishId: string, userId: string): Promise<Member> {
    const res = await appClient.get<ApiMember>(`parishes/${parishId}/members/${userId}`);
    return normalizeMember(res);
}

export async function updateMember(parishId: string, userId: string, payload: UpdateMemberPayload): Promise<Member> {
    const res = await appClient.patch<ApiMember>(`parishes/${parishId}/members/${userId}`, payload);
    return normalizeMember(res);
}

/**
 * GET .../members/:userId/donations (CLERK+) — one parishioner's gifts to this parish, newest first,
 * server-paginated. `summary` covers every gift in the date range, not just this page.
 */
export async function fetchMemberDonations(parishId: string, userId: string, query: MemberDonationsQuery): Promise<MemberDonationsPage> {
    const params = buildPageQuery(query.page, query.pageSize, { from: query.from, to: query.to });
    const res = await appClient.get<MemberDonationsPage>(`parishes/${parishId}/members/${userId}/donations?${params.toString()}`);
    return {
        items: res.items || [],
        total: res.total ?? 0,
        page: res.page ?? query.page,
        pageSize: res.pageSize ?? query.pageSize,
        summary: res.summary ?? { count: 0, netCents: 0, lastDonationAt: null },
    };
}
